import React, { useEffect, useMemo, useState, Component } from 'react';
import { computeTransactionViewModel, assertConsistency, getRowNetImpact, type LedgerRow, type Transaction } from '../utils/transactionViewModel';
import { buildScenario, DEFAULT_SCENARIO_ID, type TransactionScenario } from '../utils/transactionScenarios';
interface Badge {
  tone?: 'emerald' | 'amber' | 'red' | 'slate' | 'blue' | 'violet';
  children: React.ReactNode;
}
function Badge({
  tone = 'slate',
  children
}: Badge) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    red: 'bg-red-50 text-red-700 ring-red-200',
    slate: 'bg-slate-50 text-slate-700 ring-slate-200',
    blue: 'bg-blue-50 text-blue-700 ring-blue-200',
    violet: 'bg-violet-50 text-violet-700 ring-violet-200'
  };
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${tones[tone] || tones.slate}`} data-id="element-4576">
      {children}
    </span>;
}
interface MoneyProps {
  value: number;
  currency?: string;
}
function Money({
  value,
  currency = 'THB'
}: MoneyProps) {
  const abs = Math.abs(value);
  const formatted = `${abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${currency}`;
  return <span className="text-slate-900" data-id="element-4577">
      {value < 0 ? `−${formatted}` : formatted}
    </span>;
}
function DeltaMoney({
  value,
  currency = 'THB'
}: MoneyProps) {
  const abs = Math.abs(value);
  const formatted = `${abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${currency}`;
  const isNeg = value < 0;
  return <span className={isNeg ? 'text-red-700' : 'text-emerald-700'} data-id="element-4578">
      {isNeg ? `−${formatted}` : `+${formatted}`}
    </span>;
}
interface CardProps {
  title?: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}
function Card({
  title,
  subtitle,
  right,
  children
}: CardProps) {
  return <div className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-200" data-id="element-4579">
      {(title || subtitle || right) && <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4" data-id="element-4580">
          <div data-id="element-4581">
            {title && <div className="text-sm font-semibold text-slate-900" data-id="element-4582">
                {title}
              </div>}
            {subtitle && <div className="mt-1 text-xs text-slate-600" data-id="element-4583">{subtitle}</div>}
          </div>
          {right && <div className="shrink-0" data-id="element-4584">{right}</div>}
        </div>}
      <div className="px-5 py-4" data-id="element-4585">{children}</div>
    </div>;
}
interface RowProps {
  label: string;
  value: React.ReactNode;
  hint?: string;
}
function Row({
  label,
  value,
  hint
}: RowProps) {
  return <div className="flex items-start justify-between gap-4 py-2" data-id="element-4586">
      <div data-id="element-4587">
        <div className="text-sm text-slate-700" data-id="element-4588">{label}</div>
        {hint && <div className="mt-0.5 text-xs text-slate-500" data-id="element-4589">{hint}</div>}
      </div>
      <div className="text-sm font-medium text-slate-900" data-id="element-4590">{value}</div>
    </div>;
}
interface TableProps {
  children: React.ReactNode;
}
function Table({
  children
}: TableProps) {
  return <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200" data-id="element-4591">
      {children}
    </div>;
}
interface ThProps {
  children: React.ReactNode;
  align?: 'left' | 'right';
}
function Th({
  children,
  align = 'left'
}: ThProps) {
  return <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 ${align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-4592">
      {children}
    </th>;
}
interface TdProps {
  children: React.ReactNode;
  align?: 'left' | 'right';
}
function Td({
  children,
  align = 'left'
}: TdProps) {
  return <td className={`px-4 py-3 text-sm text-slate-800 ${align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-4593">
      {children}
    </td>;
}
interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
function Drawer({
  open,
  onClose,
  title,
  children
}: DrawerProps) {
  if (!open) return null;
  return <div className="fixed inset-0 z-50" data-id="element-4594">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} data-id="element-4595" />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl overflow-y-auto" data-id="element-4596">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4" data-id="element-4597">
          <div className="text-sm font-semibold text-slate-900" data-id="element-4598">{title}</div>
          <button className="rounded-lg px-2 py-1 text-sm text-slate-600 hover:bg-slate-100" onClick={onClose} data-id="element-4599">
            Close
          </button>
        </div>
        <div className="px-5 py-4" data-id="element-4600">{children}</div>
      </div>
    </div>;
}
function fmtDate(dateStr: string) {
  return dateStr || '—';
}
interface LedgerRow {
  id: string;
  posting: string;
  attemptId: string | null;
  type: 'payment' | 'fee' | 'refund' | 'dispute' | 'reserve_hold' | 'reserve_release' | 'dispute_reversal';
  gross: number | null;
  feeExTax: number | null;
  taxAmount: number | null;
  status: 'Posted' | 'Pending' | 'Scheduled';
  effectiveAt: string;
  settlement: {
    kind: 'payout' | 'reserve';
    id: string;
    date?: string;
    releaseDate?: string;
    status: string;
    amountInBucket: number;
  } | null;
  feeBreakdown: {
    jurisdiction: string;
    items: Array<{
      label: string;
      amountExTax: number;
      taxAmount: number;
    }>;
  } | null;
}
interface TransactionFinancialBreakdownProps {
  transactionId?: string;
  scenarioId?: string;
  currency?: string;
}
export function TransactionFinancialBreakdown({
  transactionId,
  scenarioId = DEFAULT_SCENARIO_ID,
  currency = 'THB'
}: TransactionFinancialBreakdownProps) {
  const [taxDisplay, setTaxDisplay] = useState<'included' | 'separate'>('included');
  const asOf = new Date().toLocaleString('en-GB', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
  // Load scenario data (single source of truth)
  const scenario = useMemo(() => {
    try {
      return buildScenario(scenarioId);
    } catch (error) {
      console.error('Failed to load scenario:', scenarioId, error);
      // Return a fallback minimal scenario
      return {
        scenarioId: 'fallback',
        description: 'Fallback scenario',
        transaction: {
          id: '0',
          transactionId: transactionId || 'TXN-FALLBACK',
          amount: 0,
          currency: currency,
          date: new Date().toISOString()
        },
        ledgerRows: []
      };
    }
  }, [scenarioId, transactionId, currency]);
  const transaction = scenario.transaction;
  const ledger = scenario.ledgerRows || [];
  const [drawer, setDrawer] = useState<{
    open: boolean;
    ledgerId: string | null;
  }>({
    open: false,
    ledgerId: null
  });
  const selected = useMemo(() => ledger.find(x => x.id === drawer.ledgerId) || null, [drawer.ledgerId, ledger]);
  // Compute view model from ledger (single source of truth)
  const viewModel = useMemo(() => {
    try {
      const vm = computeTransactionViewModel(transaction, ledger, taxDisplay);
      // Ensure all required properties exist
      return {
        ...vm,
        timelineItems: vm.timelineItems || [],
        payoutBuckets: vm.payoutBuckets || [],
        reserveBucket: vm.reserveBucket || null,
        summary: vm.summary || {
          grossImpact: 0,
          feesPostedEx: 0,
          feesPendingEx: 0,
          taxPosted: 0,
          taxPending: 0,
          feesPostedDisplay: 0,
          feesPendingDisplay: 0,
          reserveHeld: 0,
          nextReserveRelease: null,
          netPosted: 0,
          paidOut: 0,
          scheduledPayoutTotal: 0
        }
      };
    } catch (error) {
      console.error('Failed to compute view model:', error);
      // Return a safe fallback
      return {
        headerAmount: 0,
        headerState: 'Unknown',
        summary: {
          grossImpact: 0,
          feesPostedEx: 0,
          feesPendingEx: 0,
          taxPosted: 0,
          taxPending: 0,
          feesPostedDisplay: 0,
          feesPendingDisplay: 0,
          reserveHeld: 0,
          nextReserveRelease: null,
          netPosted: 0,
          paidOut: 0,
          scheduledPayoutTotal: 0
        },
        payoutBuckets: [],
        reserveBucket: null,
        timelineItems: []
      };
    }
  }, [transaction, ledger, taxDisplay]);
  // Run consistency checks on mount and when data changes
  useEffect(() => {
    console.log(`\n=== Scenario: ${scenario.scenarioId} ===`);
    console.log(`Description: ${scenario.description}`);
    assertConsistency(viewModel, transaction, ledger);
  }, [viewModel, transaction, ledger, scenario]);
  const computed = viewModel.summary;
  const flags = [];
  if (Math.abs(computed.feesPendingEx) > 0.0001 || Math.abs(computed.taxPending) > 0.0001) flags.push({
    tone: 'amber' as const,
    label: 'Fees pending'
  });
  if (computed.reserveHeld > 0.0001) flags.push({
    tone: 'violet' as const,
    label: 'Reserve active'
  });
  if (viewModel.payoutBuckets.length > 0) flags.push({
    tone: 'blue' as const,
    label: 'Scheduled payouts'
  });
  // Helper functions for ledger table display
  function feeDisplay(row: LedgerRow) {
    if (row.type !== 'fee') return null;
    const ex = row.feeExTax ?? 0;
    const tax = row.taxAmount ?? 0;
    return taxDisplay === 'included' ? ex + tax : ex;
  }
  function taxDisplayAmount(row: LedgerRow) {
    if (row.type !== 'fee') return null;
    return row.taxAmount ?? 0;
  }
  function netImpact(row: LedgerRow) {
    if (row.type === 'fee') {
      const ex = row.feeExTax ?? 0;
      const tax = row.taxAmount ?? 0;
      return ex + tax;
    }
    if (row.type === 'payment' || row.type === 'refund' || row.type === 'dispute' || row.type === 'dispute_reversal') {
      return row.gross ?? 0;
    }
    if (row.type === 'reserve_hold') return -200;
    if (row.type === 'reserve_release') return 200;
    return 0;
  }
  return <div className="space-y-5" data-id="element-4601">
      {/* Recent activity timeline - moved to top */}
      <Card title="Recent activity" subtitle="Transaction timeline with all key events and amounts" data-id="element-4602">
        <div className="space-y-4" data-id="element-4603">
          {viewModel.timelineItems && viewModel.timelineItems.length > 0 ? viewModel.timelineItems.map((t, idx) => {
          const isLast = idx === viewModel.timelineItems.length - 1;
          return <div key={idx} className="relative" data-id="element-4604">
                  {!isLast && <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-slate-200" data-id="element-4605" />}
                  <div className="flex gap-4" data-id="element-4606">
                    <div className="relative flex-shrink-0" data-id="element-4607">
                      <div className={`h-6 w-6 rounded-full flex items-center justify-center ring-4 ring-white ${t.type === 'positive' ? 'bg-emerald-100' : t.type === 'negative' ? 'bg-red-100' : 'bg-slate-100'}`} data-id="element-4608">
                        <div className={`h-2.5 w-2.5 rounded-full ${t.type === 'positive' ? 'bg-emerald-600' : t.type === 'negative' ? 'bg-red-600' : 'bg-slate-400'}`} data-id="element-4609" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 pb-4" data-id="element-4610">
                      <div className="flex items-start justify-between gap-3" data-id="element-4611">
                        <div className="flex-1" data-id="element-4612">
                          <div className="text-sm font-medium text-slate-900" data-id="element-4613">
                            {t.label}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-600" data-id="element-4614">
                            {t.at}
                          </div>
                        </div>
                        {t.amount !== null && <div className="flex-shrink-0" data-id="element-4615">
                            <div className={`text-sm font-semibold ${t.type === 'positive' ? 'text-emerald-700' : t.type === 'negative' ? 'text-red-700' : 'text-slate-700'}`} data-id="element-4616">
                              {t.type === 'positive' ? '+' : t.type === 'negative' ? '−' : ''}
                              <Money value={t.amount} currency={currency} data-id="element-4617" />
                            </div>
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>;
        }) : <div className="text-sm text-slate-600" data-id="element-4618">No activity recorded</div>}
        </div>
      </Card>

      {/* Financial summary */}
      <Card title="Financial summary" subtitle={`As of ${asOf}. Values reflect posted ledger entries; pending rows may change totals later.`} right={<div className="flex flex-wrap justify-end gap-2" data-id="element-4620">
            {flags.map(f => <Badge key={f.label} tone={f.tone} data-id="element-4621">
                {f.label}
              </Badge>)}
          </div>} data-id="element-4619">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-4622">
          {/* Balance impact */}
          <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-4623">
            <div className="flex items-center justify-between gap-3" data-id="element-4624">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-4625">
                Impact on merchant balance
              </div>
              <div className="flex items-center gap-2" data-id="element-4626">
                <span className="text-xs text-slate-600" data-id="element-4627">Tax display</span>
                <div className="inline-flex rounded-full bg-white p-1 ring-1 ring-slate-200" data-id="element-4628">
                  <button onClick={() => setTaxDisplay('included')} className={`rounded-full px-3 py-1 text-xs font-medium ${taxDisplay === 'included' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-4629">
                    Included
                  </button>
                  <button onClick={() => setTaxDisplay('separate')} className={`rounded-full px-3 py-1 text-xs font-medium ${taxDisplay === 'separate' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-4630">
                    Separate
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-3 divide-y divide-slate-200" data-id="element-4631">
              <Row label="Gross impact" hint="Customer-facing amounts (payments/refunds/disputes)." value={<DeltaMoney value={computed.grossImpact} currency={currency} data-id="element-4633" />} data-id="element-4632" />
              {taxDisplay === 'included' ? <>
                  <Row label="Fees (posted, incl. tax where applicable)" hint="Includes tax for taxable fee components." value={<DeltaMoney value={computed.feesPostedDisplay} currency={currency} data-id="element-4635" />} data-id="element-4634" />
                </> : <>
                  <Row label="Fees (posted, ex tax)" hint="Fee amounts excluding tax." value={<DeltaMoney value={computed.feesPostedEx} currency={currency} data-id="element-4637" />} data-id="element-4636" />
                  <Row label="Tax on fees (posted)" hint="Tax booked on fee components (some fee types may have 0 tax)." value={<DeltaMoney value={computed.taxPosted} currency={currency} data-id="element-4639" />} data-id="element-4638" />
                </>}
              <Row label="Reserve held" hint={computed.nextReserveRelease ? `Scheduled release on ${computed.nextReserveRelease}.` : 'No reserve active.'} value={computed.reserveHeld > 0.0001 ? <DeltaMoney value={-computed.reserveHeld} currency={currency} data-id="element-4641" /> : <span className="text-sm text-slate-500" data-id="element-4642">—</span>} data-id="element-4640" />
              <Row label="Net impact (posted so far)" hint="Sum of posted ledger entries (truth)." value={<DeltaMoney value={computed.netPosted} currency={currency} data-id="element-4644" />} data-id="element-4643" />
            </div>
          </div>

          {/* Cash movement */}
          <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-4645">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-4646">
              Cash movement
            </div>
            <div className="mt-3 divide-y divide-slate-200" data-id="element-4647">
              <Row label="Paid out" hint="Amount already included in completed payouts." value={<DeltaMoney value={computed.paidOut} currency={currency} data-id="element-4649" />} data-id="element-4648" />
              <Row label="Scheduled in upcoming payouts" hint="Sum of amounts mapped to scheduled payout buckets." value={<DeltaMoney value={computed.scheduledPayoutTotal} currency={currency} data-id="element-4651" />} data-id="element-4650" />
              <Row label="Held in reserve" hint="Funds temporarily withheld per reserve policy." value={computed.reserveHeld > 0.0001 ? <DeltaMoney value={-computed.reserveHeld} currency={currency} data-id="element-4653" /> : <span className="text-sm text-slate-500" data-id="element-4654">—</span>} data-id="element-4652" />
              <Row label="Next scheduled payout" hint="Where the next part of this transaction will land." value={viewModel.payoutBuckets.length ? <span className="text-sm text-slate-900" data-id="element-4656">
                      {viewModel.payoutBuckets[0].id} •{' '}
                      {viewModel.payoutBuckets[0].date}
                    </span> : <span className="text-sm text-slate-500" data-id="element-4657">—</span>} data-id="element-4655" />
            </div>
          </div>
        </div>
      </Card>

      {/* Unified ledger table */}
      <Table data-id="element-4658">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4" data-id="element-4659">
          <div data-id="element-4660">
            <div className="text-sm font-semibold text-slate-900" data-id="element-4661">
              Ledger for this transaction
            </div>
            <div className="mt-1 text-xs text-slate-600" data-id="element-4662">
              Every posting that affects the merchant balance—payments, refunds,
              disputes, fees, reserves, and reversals.
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-4663">
            <button className="rounded-xl bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50" data-id="element-4664">
              Export CSV
            </button>
          </div>
        </div>
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3" data-id="element-4665">
          <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-4666">
            <div className="text-xs text-slate-600" data-id="element-4667">
              Fee tax display:{' '}
              <span className="font-semibold text-slate-900" data-id="element-4668">
                {taxDisplay === 'included' ? 'Included in fee' : 'Shown separately'}
              </span>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-4669">
          <table className="min-w-full" data-id="element-4670">
            <thead className="bg-white" data-id="element-4671">
              <tr className="border-b border-slate-200" data-id="element-4672">
                <Th data-id="element-4673">Posting</Th>
                <Th align="right" data-id="element-4674">Gross</Th>
                <Th align="right" data-id="element-4675">Fee</Th>
                {taxDisplay === 'separate' && <Th align="right" data-id="element-4676">Tax</Th>}
                <Th align="right" data-id="element-4677">Net impact</Th>
                <Th data-id="element-4678">Status</Th>
                <Th data-id="element-4679">Effective</Th>
                <Th data-id="element-4680">Settlement</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-4681">
              {ledger.map(r => {
              const feeShown = feeDisplay(r);
              const taxShown = taxDisplayAmount(r);
              const net = getRowNetImpact(r);
              const settlement = r.settlement;
              const settlementUi = settlement?.kind === 'payout' ? <div className="space-y-1" data-id="element-4682">
                      <div className="text-sm font-medium text-slate-900" data-id="element-4683">
                        {settlement.id}
                      </div>
                      <div className="text-xs text-slate-600" data-id="element-4684">
                        {settlement.date} • {settlement.status}
                      </div>
                    </div> : settlement?.kind === 'reserve' ? <div className="space-y-1" data-id="element-4685">
                      <div className="text-sm font-medium text-slate-900" data-id="element-4686">
                        Reserve {settlement.id}
                      </div>
                      <div className="text-xs text-slate-600" data-id="element-4687">
                        Releases {fmtDate(settlement.releaseDate || '')}
                      </div>
                    </div> : <span className="text-slate-500" data-id="element-4688">Not yet included</span>;
              return <tr key={r.id} className="hover:bg-slate-50" data-id="element-4689">
                    <Td data-id="element-4690">
                      <div className="font-medium text-slate-900" data-id="element-4691">
                        {r.posting}
                      </div>
                      <div className="mt-1 text-xs text-slate-500" data-id="element-4692">{r.id}</div>
                    </Td>
                    <Td align="right" data-id="element-4693">
                      {r.gross == null ? <span className="text-slate-500" data-id="element-4694">—</span> : <DeltaMoney value={r.gross} currency={currency} data-id="element-4695" />}
                    </Td>
                    <Td align="right" data-id="element-4696">
                      {r.type !== 'fee' ? <span className="text-slate-500" data-id="element-4697">—</span> : <div className="flex items-center justify-end gap-2" data-id="element-4698">
                          <DeltaMoney value={feeShown!} currency={currency} data-id="element-4699" />
                          {r.feeBreakdown && <button onClick={() => setDrawer({
                      open: true,
                      ledgerId: r.id
                    })} className="text-xs text-blue-600 hover:text-blue-700 hover:underline" title="View fee breakdown" data-id="element-4700">
                              Details
                            </button>}
                        </div>}
                    </Td>
                    {taxDisplay === 'separate' && <Td align="right" data-id="element-4701">
                        {r.type !== 'fee' ? <span className="text-slate-500" data-id="element-4702">—</span> : taxShown === 0 ? <span className="text-slate-500" data-id="element-4703">0.00</span> : <DeltaMoney value={taxShown!} currency={currency} data-id="element-4704" />}
                      </Td>}
                    <Td align="right" data-id="element-4705">
                      <DeltaMoney value={net} currency={currency} data-id="element-4706" />
                    </Td>
                    <Td data-id="element-4707">
                      {r.status === 'Posted' ? <Badge tone="emerald" data-id="element-4708">Posted</Badge> : r.status === 'Pending' ? <Badge tone="amber" data-id="element-4709">Pending</Badge> : <Badge tone="blue" data-id="element-4710">Scheduled</Badge>}
                    </Td>
                    <Td data-id="element-4711">
                      <div className="text-sm text-slate-800" data-id="element-4712">
                        {r.effectiveAt}
                      </div>
                    </Td>
                    <Td data-id="element-4713">{settlementUi}</Td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Table>

      {/* Payout / transfer story */}
      <Card title="Payout / transfer story" subtitle="Explains where money lands: payouts vs reserve, and how asynchronous fees/taxes affect reconciliation." data-id="element-4714">
        <div className="space-y-3" data-id="element-4715">
          {viewModel.payoutBuckets && viewModel.payoutBuckets.length > 0 ? viewModel.payoutBuckets.map(p => <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-4716">
                <div data-id="element-4717">
                  <div className="text-sm font-semibold text-slate-900" data-id="element-4718">
                    {p.id}
                  </div>
                  <div className="mt-1 text-xs text-slate-600" data-id="element-4719">
                    {p.date} • {p.status} • {p.rows.length} postings
                  </div>
                </div>
                <div className="text-sm font-semibold" data-id="element-4720">
                  <DeltaMoney value={p.total} currency={currency} data-id="element-4721" />
                </div>
              </div>) : <div className="text-sm text-slate-600" data-id="element-4722">No scheduled payouts</div>}
          {viewModel.reserveBucket && <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-violet-50 p-4 ring-1 ring-violet-200" data-id="element-4723">
              <div data-id="element-4724">
                <div className="text-sm font-semibold text-violet-900" data-id="element-4725">
                  Reserve {viewModel.reserveBucket.id}
                </div>
                <div className="mt-1 text-xs text-violet-900/80" data-id="element-4726">
                  Held now • Releases {viewModel.reserveBucket.releaseAt || '—'}
                </div>
              </div>
              <div className="text-sm font-semibold text-violet-900" data-id="element-4727">
                <DeltaMoney value={-viewModel.reserveBucket.heldNow} currency={currency} data-id="element-4728" />
              </div>
            </div>}
        </div>
      </Card>

      {/* Fee breakdown drawer */}
      <Drawer open={drawer.open} onClose={() => setDrawer({
      open: false,
      ledgerId: null
    })} title={selected ? `Fee breakdown — ${selected.posting}` : 'Fee breakdown'} data-id="element-4729">
        {!selected || !selected.feeBreakdown ? <div className="text-sm text-slate-600" data-id="element-4730">
            No fee details available.
          </div> : <div className="space-y-4" data-id="element-4731">
            <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-4732">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-4733">
                Jurisdiction
              </div>
              <div className="mt-2 text-sm font-semibold text-slate-900" data-id="element-4734">
                {selected.feeBreakdown.jurisdiction}
              </div>
              <div className="mt-2 text-xs text-slate-600" data-id="element-4735">
                Display mode:{' '}
                <span className="font-semibold text-slate-900" data-id="element-4736">
                  {taxDisplay === 'included' ? 'Tax included in fee' : 'Tax shown separately'}
                </span>
              </div>
            </div>

            <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200" data-id="element-4737">
              <div className="text-sm font-semibold text-slate-900" data-id="element-4738">
                Components
              </div>
              <div className="mt-3 divide-y divide-slate-200" data-id="element-4739">
                {selected.feeBreakdown.items.map((it, idx) => <div key={idx} className="grid grid-cols-3 items-center gap-3 py-2" data-id="element-4740">
                    <div className="col-span-1 text-sm text-slate-700" data-id="element-4741">
                      {it.label}
                    </div>
                    <div className="col-span-1 text-right text-sm font-medium" data-id="element-4742">
                      <DeltaMoney value={it.amountExTax} currency={currency} data-id="element-4743" />
                    </div>
                    <div className="col-span-1 text-right text-sm font-medium" data-id="element-4744">
                      {it.taxAmount === 0 ? <span className="text-slate-500" data-id="element-4745">0.00</span> : <DeltaMoney value={it.taxAmount} currency={currency} data-id="element-4746" />}
                    </div>
                    <div className="col-span-3 -mt-1 text-xs text-slate-500" data-id="element-4747">
                      Ex tax • Tax
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-4748">
              <div className="flex items-center justify-between" data-id="element-4749">
                <div className="text-sm font-semibold text-slate-900" data-id="element-4750">
                  Totals
                </div>
                <Badge tone={selected.status === 'Posted' ? 'emerald' : selected.status === 'Pending' ? 'amber' : 'blue'} data-id="element-4751">
                  {selected.status}
                </Badge>
              </div>
              <div className="mt-3 divide-y divide-slate-200" data-id="element-4752">
                <Row label="Fee (ex tax)" value={<DeltaMoney value={selected.feeExTax ?? 0} currency={currency} data-id="element-4754" />} data-id="element-4753" />
                <Row label="Tax on fee" value={<DeltaMoney value={selected.taxAmount ?? 0} currency={currency} data-id="element-4756" />} data-id="element-4755" />
                <Row label="Total fee (inc tax)" value={<DeltaMoney value={(selected.feeExTax ?? 0) + (selected.taxAmount ?? 0)} currency={currency} data-id="element-4758" />} data-id="element-4757" />
              </div>
            </div>
          </div>}
      </Drawer>
    </div>;
}